import { useState, useEffect } from 'react';

/**
 * A hook to check if the current screen size matches a given media query.
 *
 * @param query - The media query to match
 * @param defaultValue - The default value to return if the window is undefined
 * @returns true if the media query matches, false otherwise
 * @example
 * const isLargeScreen = useMediaQuery('(min-width: 501px)', true);
 * if (isLargeScreen) {
 *   return <div>This is a large screen</div>;
 * }
 * return <div>This is a small screen</div>;
 */
const useMediaQuery = (query: string, defaultValue = false): boolean => {
	const [matches, setMatches] = useState(defaultValue);

	useEffect(() => {
		const mediaQuery = window.matchMedia(query);
		setMatches(mediaQuery.matches);

		const handleChange = (event: MediaQueryListEvent) => {
			setMatches(event.matches);
		};

		mediaQuery.addEventListener('change', handleChange);

		return () => {
			mediaQuery.removeEventListener('change', handleChange);
		};
	}, [query]);

	return matches;
};

export default useMediaQuery;
